import { Flex, ProgressBar, Text, Color, Skeleton } from "src/elements";
import { getProgressColor } from "src/elements/ProgressBar";

import { textVariants } from "src/elements/Text/store/variants";

interface Props {
  title?: string;
  percent?: number | null;
  /**
   * For metrics that are not percentages — a currency amount, say. Pass the
   * text already formatted and it is shown in place of the percentage; pass
   * null to show the same "-" a missing percentage gets. The bar stays empty
   * because an amount has no ceiling to fill it against, and the colour scale
   * stays off because 80/50 mean nothing outside a percentage.
   */
  value?: string | null;
  height?: "default" | "small" | "big" | string;
  isInline?: boolean;
  isLoading?: boolean;
}

export const Metric = ({ title, percent, value, height, isInline, isLoading }: Props) => {
  const isAmount = value !== undefined;
  const isValid = isAmount ? value !== null : percent !== undefined;
  const color = isAmount
    ? (isValid && Color.textMain) || Color.textDisabled
    : getProgressColor(percent);
  const size =
    (height === "small" && "6px") ||
    (height === "default" && "12px") ||
    (height === "big" && "18px") ||
    height;

  return (
    <Flex width="100%" direction="column" gap={(height === "small" && "1px") || "5px"}>
      <ProgressBar
        percent={percent}
        color={color}
        height={size}
        isLoading={isLoading}
        isInline={isInline}
      />

      {title && (
        <>
          {/* Titles are laid out in equal-width tracks that can be squeezed
              narrower than the words in them. Truncating keeps each label
              inside its own tile; without this a cramped strip prints two
              titles on top of each other and neither can be read. */}
          <Text
            variant={(height === "small" && "caption2") || "h6"}
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            textTransform="uppercase"
            color={color}
          >
            {title}:
          </Text>

          {isLoading && (
            <Skeleton
              width="70px"
              height={textVariants[(height === "small" && "caption2") || "h4"].lineHeight}
            />
          )}

          {!isLoading && (
            <Text
              variant={(height === "small" && "caption2") || "h4"}
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              textTransform="uppercase"
              color={isValid ? Color.textMain : Color.textDisabled}
              isLoading={isLoading}
            >
              {isAmount ? (value ?? "-") : percent ? `${Math.round(percent)}%` : "-"}
            </Text>
          )}
        </>
      )}
    </Flex>
  );
};
